import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { DAY_LABELS, availabilityWindowError } from '../../lib/appointments/availability';
import type { BookingAvailabilityWindow } from '../../types/appointments';

// Opening hours for a booking calendar — the hours a slot may be OFFERED in,
// which is not the same thing as the hours anyone is free.
//
// ⚠️ THE TIMEZONE LIVES ON THE CALENDAR, NOT HERE. It is picked once in Basic
// details, because it applies to every window and to every confirmation the bot
// sends — and because a per-window zone would be a way to get one day wrong
// without noticing.
//
// Several windows per day are supported (a 9–12 / 13–17 split around lunch),
// which is why this is not the lead bot's one-row-per-day editor.

export function AvailabilityEditor({
  value,
  onChange,
  timezone,
}: {
  value: BookingAvailabilityWindow[];
  onChange: (next: BookingAvailabilityWindow[]) => void;
  timezone: string;
}) {
  const windowsFor = (day: number) => value.filter((w) => w.day === day);

  const replaceDay = (day: number, windows: BookingAvailabilityWindow[]) => {
    const next = [...value.filter((w) => w.day !== day), ...windows];
    next.sort((a, b) => a.day - b.day || a.startTime.localeCompare(b.startTime));
    onChange(next);
  };

  const setOpen = (day: number, open: boolean) => {
    replaceDay(day, open ? [{ day, startTime: '09:00', endTime: '17:00' }] : []);
  };

  const patchWindow = (day: number, index: number, patch: Partial<BookingAvailabilityWindow>) => {
    const windows = windowsFor(day).map((w, i) => (i === index ? { ...w, ...patch } : w));
    replaceDay(day, windows);
  };

  const addWindow = (day: number) => {
    replaceDay(day, [...windowsFor(day), { day, startTime: '13:00', endTime: '17:00' }]);
  };

  const removeWindow = (day: number, index: number) => {
    replaceDay(
      day,
      windowsFor(day).filter((_, i) => i !== index),
    );
  };

  return (
    <div>
      <p className="mb-3 text-sm text-muted-foreground">
        The hours a booking may be offered in, in this calendar&apos;s local time
        {timezone ? ` (${timezone})` : ''}. Whether anyone is actually free within them comes
        from their own calendar.
      </p>

      <div className="space-y-2">
        {DAY_LABELS.map((label, day) => {
          const windows = windowsFor(day);
          const open = windows.length > 0;
          return (
            <div key={label} className="flex items-start gap-3">
              <label className="flex w-32 shrink-0 items-center gap-2 pt-1.5 text-sm">
                <Checkbox
                  checked={open}
                  onCheckedChange={(checked) => setOpen(day, checked === true)}
                  aria-label={`${label} open`}
                />
                {label}
              </label>

              <div className="min-w-0 flex-1 space-y-1.5">
                {!open && <p className="pt-1.5 text-xs text-muted-foreground">Closed</p>}
                {windows.map((window, index) => {
                  const error = availabilityWindowError(window);
                  return (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        type="time"
                        className="h-8 w-32"
                        value={window.startTime}
                        onChange={(e) => patchWindow(day, index, { startTime: e.target.value })}
                        aria-label={`${label} window ${index + 1} opening time`}
                      />
                      <span className="text-xs text-muted-foreground">to</span>
                      <Input
                        type="time"
                        className="h-8 w-32"
                        value={window.endTime}
                        onChange={(e) => patchWindow(day, index, { endTime: e.target.value })}
                        aria-label={`${label} window ${index + 1} closing time`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeWindow(day, index)}
                        aria-label={`Remove ${label} window ${index + 1}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      {/* ⚠️ Stated inline rather than only blocking the save: an
                          unusable window is treated as CLOSED by the engine, so
                          silence here would mean a day quietly offering nothing. */}
                      {error && <span className="text-xs text-amber-700">{error}</span>}
                    </div>
                  );
                })}
                {open && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2"
                    onClick={() => addWindow(day)}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    Add another window
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {value.length === 0 && (
        // ⚠️ Not an error, but it must not be discovered later: a calendar with no
        // open hours can never offer a slot, and the bot will hand every booking
        // request to a human.
        <p className="mt-3 text-xs text-amber-700">
          No days are open, so this calendar can never offer a time. Every booking request will
          go to a person instead.
        </p>
      )}
    </div>
  );
}
